import { defineStore } from 'pinia'

const LANGUAGE_STORAGE_KEY = 'selectedLanguage'
const DEFAULT_LANGUAGE = 'ru'
const SUPPORTED_LANGUAGES = ['ru', 'kz', 'en'] as const

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)
}

function getInitialLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }

  try {
    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)

    if (storedLanguage && isSupportedLanguage(storedLanguage)) {
      return storedLanguage
    }
  }
  catch {
    // Keep default language when localStorage is unavailable.
  }

  return DEFAULT_LANGUAGE
}

interface LangState {
  lang: SupportedLanguage
}

interface LangActions {
  setSelectedLanguage: (selectedLanguage: string) => void
  getSelectedLanguage: () => string
}

export const useLangStore = defineStore<
  'lang',
  LangState,
  Record<string, any>,
  LangActions
>({
  id: 'lang',
  state: (): LangState => ({
    lang: getInitialLanguage(),
  }),
  actions: {
    setSelectedLanguage(selectedLanguage: string) {
      const nextLanguage = isSupportedLanguage(selectedLanguage)
        ? selectedLanguage
        : DEFAULT_LANGUAGE

      this.lang = nextLanguage

      if (typeof window === 'undefined') {
        return
      }

      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage)
      }
      catch {
        // Ignore write failures to avoid blocking language switch.
      }
    },
    getSelectedLanguage(): string {
      return this.lang
    },
  },
})
